import {
  Box,
  Button,
  Icon,
  SxProps,
  Theme,
  Typography
} from "@outschool/backpack";
import { faStar, fasStar } from "@outschool/icons";
import { useLocale, useTranslation } from "@outschool/localization";
import { enrolledPath } from "@outschool/routes";
import { dayjs, formatDateTimeWithWeekday } from "@outschool/time";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { AvatarImage, DEFAULT_AVATAR } from "@outschool/ui-components-shared";
import { useIsLarge, useNavigation } from "@outschool/ui-utils";
import React, { useState } from "react";

import * as Enrollment from "../../shared/Enrollment";
import {
  RecentlyCompletedClassesQueryQuery,
  RecentlyCompletedClassesQueryQueryVariables
} from "../../shared/generated/graphql";
import * as Routes from "../../shared/Routes";
import * as Section from "../../shared/Section";
import { useTimeZone } from "../providers/TimeZoneProvider";
import { RemainingMeetingsChip } from "./Chips/RemainingMeetingsChip";
import { SectionStatusChip } from "./Chips/SectionStatusChip";
import Link from "./Link";
import {
  ClassDescriptionProps,
  ClassImage,
  ClassProps,
  ClassTeacher,
  EnrollmentType,
  SectionType,
  Seperator
} from "./loggedInHome/LoggedInHomeMyClassesList";

const RecentlyCompletedClassDescription = ({
  enrollment,
  learner,
  section
}: ClassDescriptionProps) => {
  const { activity, end_time, meetings, nextOngoingMeeting } = section;
  const isLarge = useIsLarge();
  const { timeZone } = useTimeZone();
  const { t } = useTranslation("client\\routes\\RecentlyCompletedClassesList");

  const enrollmentEndTime = dayjs.min(
    enrollment.withdrawn_at
      ? dayjs(enrollment.withdrawn_at)
      : Enrollment.getEnrollmentEndTimeForTranscript(enrollment),
    dayjs()
  );
  const lastSessionTime = enrollmentEndTime.isValid()
    ? enrollmentEndTime.toDate()
    : meetings.length
    ? dayjs(meetings[meetings.length - 1].start_time).toDate()
    : undefined;
  const durationString = activity.isClub
    ? t("Unlimited access")
    : t("Last session: {{lastSessionTime}}", {
        lastSessionTime: formatDateTimeWithWeekday(lastSessionTime, timeZone)
      });
  // do not show the duration string if the next meeting starts after the enrollment has ended
  const showDurationString =
    (!enrollment.ongoingEndTime ||
      (nextOngoingMeeting &&
        dayjs(nextOngoingMeeting.start_time).isSameOrBefore(
          dayjs(enrollment?.ongoingEndTime)
        ))) &&
    lastSessionTime;

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="h6">{activity.title}</Typography>
      <Box
        flex
        sx={(theme: Theme) => ({
          flexDirection: "column",
          [theme.breakpoints.up("lg")]: {
            flexDirection: "row",
            overflow: "hidden",
            whiteSpace: "nowrap"
          }
        })}
      >
        <Box flex sx={{ alignItems: "center", gap: 6 }}>
          <AvatarImage
            avatar={learner.avatar || DEFAULT_AVATAR}
            name={learner.name}
            sx={{ height: 24, width: 24 }}
          />
          <Typography variant="body1">{learner.name}</Typography>
        </Box>
        {showDurationString && durationString && (
          <>
            {isLarge && <Seperator />}
            <Typography variant="body1">{durationString}</Typography>
          </>
        )}
      </Box>
      {end_time ? (
        <SectionStatusChip size="small" section={section} />
      ) : (
        <RemainingMeetingsChip size="small" numRemainingMeetings={0} />
      )}
    </Box>
  );
};

interface RecentlyCompletedClassActionProps {
  section: SectionType;
}

const RecentlyCompletedClassActions = ({
  section
}: RecentlyCompletedClassActionProps) => {
  const { t } = useTranslation("client\\routes\\RecentlyCompletedClassesList");
  const navigate = useNavigation();
  const { activity, currentUserPublicReview } = section;
  const canReview =
    !currentUserPublicReview && Section.isInReviewTimeWindow(section);
  const canEdit = currentUserPublicReview?.currentUserCanEdit;
  return (
    <Box
      flex
      sx={{
        alignItems: "center"
      }}
    >
      <Button
        variant="link"
        role="link"
        size="small"
        color="inherit"
        onClick={(event: React.MouseEvent<HTMLElement>) => {
          // Stop link click from bubbling to link underneath
          event.preventDefault();
          navigate(Routes.sectionFeedbackPath(activity, section));
        }}
        disabled={!canReview && !canEdit}
        startIcon={
          <Icon icon={canEdit || currentUserPublicReview ? fasStar : faStar} />
        }
      >
        {canEdit || currentUserPublicReview
          ? t("Edit Review")
          : t("Add Review")}
      </Button>
    </Box>
  );
};

const RecentlyCompletedClass = ({ enrollment, sx: sxProps }: ClassProps) => {
  const locale = useLocale();

  const { section, learner } = enrollment;
  const { activity, leader: sectionLeader } = section;

  const { details, leader: activityLeader } = activity;
  const { photo } = details;

  // Favor section leaders in the event it is a seller org
  const leader = sectionLeader || activityLeader;

  if (!learner) {
    return null;
  }

  return (
    <Box
      flex
      sx={[
        (theme: Theme) => ({
          paddingY: 16,
          paddingX: 24,
          gap: 8,
          borderColor: "grey.100",
          borderStyle: "solid",
          borderWidth: "1px",
          borderRadius: "8px",
          justifyContent: "space-between",
          flexDirection: "column",
          [theme.breakpoints.up("md")]: {
            flexDirection: "row",
            gap: 0
          },
          // reset link styles
          textDecoration: "none",
          color: "unset",
          "&:hover:not(:disabled), &:active:not(:disabled), &:focus:not(:disabled)":
            {
              textDecoration: "none",
              color: "unset"
            }
        }),
        ...(Array.isArray(sxProps) ? sxProps : [sxProps])
      ]}
      component={Link}
      to={enrolledPath(section.uid, locale)}
    >
      <Box
        flex
        sx={(theme: Theme) => ({
          gap: 8,
          alignItems: "flex-start",
          flexDirection: "column",
          [theme.breakpoints.up("md")]: {
            gap: 24,
            alignItems: "center",
            flexDirection: "row",
            flexWrap: "wrap"
          }
        })}
      >
        <ClassImage
          src={photo ? photo : ""}
          sx={(theme: Theme) => ({
            width: "100%",
            height: "auto",
            [theme.breakpoints.up("md")]: {
              width: 93,
              height: 88
            }
          })}
        />
        <RecentlyCompletedClassDescription
          enrollment={enrollment}
          learner={learner}
          section={section}
        />
      </Box>
      <Box
        flex
        sx={(theme: Theme) => ({
          gap: 16,
          alignItems: "center",
          justifyContent: "space-between",
          [theme.breakpoints.up("md")]: {
            justifyContent: "flex-start"
          }
        })}
      >
        <ClassTeacher leader={leader} isLinkComponent={false} />
        <RecentlyCompletedClassActions section={section} />
      </Box>
    </Box>
  );
};

interface RecentlyCompletedClassesListProps {
  limitRecentlyCompletedClasses?: number | undefined;
  sx?: SxProps;
  listItemSx?: SxProps;
  filterByLearnerUid?: string | null;
}

export const recentlyCompletedClassesQuery = gql`
  query RecentlyCompletedClassesQuery(
    $end: DateTime
    $learnerUid: String
    $limit: Int
  ) {
    currentUser {
      uid
      learners {
        uid
        name
      }
      enrollmentsForTranscript(
        end: $end
        learnerUid: $learnerUid
        limit: $limit
      ) {
        uid
        confirmed_at
        withdrawn_at
        ongoing_start_time
        ongoingEndTime
        learner {
          uid
          name
          avatar
        }
        section {
          uid
          canceled_at
          start_time
          end_time
          leader {
            uid
            name
            leader_link
            photo
          }
          activity {
            uid
            title
            details {
              photo
            }
          }
          meetings {
            uid
            start_time
            end_time
          }
          currentUserPublicReview {
            uid
            currentUserCanEdit
          }
        }
      }
    }
  }
`;

export const RecentlyCompletedClassesList = ({
  limitRecentlyCompletedClasses,
  sx,
  listItemSx,
  filterByLearnerUid
}: RecentlyCompletedClassesListProps) => {
  const { t } = useTranslation("client\\routes\\RecentlyCompletedClassesList");
  const navigate = useNavigation();
  const [showClassesCompletedByDate] = useState(new Date());
  const { data, loading, error } = useQueryWithPreviousData<
    RecentlyCompletedClassesQueryQuery,
    RecentlyCompletedClassesQueryQueryVariables
  >(recentlyCompletedClassesQuery, {
    variables: {
      learnerUid: filterByLearnerUid,
      // Grabbing enough to display the limit + 1 extra, because we want to show a "View All" button if there are more
      limit: limitRecentlyCompletedClasses
        ? limitRecentlyCompletedClasses + 1
        : undefined,
      end: showClassesCompletedByDate
    }
  });

  const enrollmentsForTranscript =
    data?.currentUser?.enrollmentsForTranscript.map(e => ({
      ...e
    })) ?? [];
  const completedEnrollmentsToDisplay =
    limitRecentlyCompletedClasses != null
      ? enrollmentsForTranscript.slice(0, limitRecentlyCompletedClasses)
      : enrollmentsForTranscript;

  if (loading || (!data && !error) || !completedEnrollmentsToDisplay.length) {
    return <></>;
  }

  return (
    <>
      <Typography
        variant="h5"
        sx={{
          marginY: 32
        }}
        align="center"
      >
        {t("Recently completed")}
      </Typography>
      <Box
        flex
        sx={[
          { flexDirection: "column", gap: 24 },
          ...(Array.isArray(sx) ? sx : [sx])
        ]}
      >
        {completedEnrollmentsToDisplay.map(
          enrollment =>
            enrollment && (
              <RecentlyCompletedClass
                key={enrollment.uid}
                enrollment={enrollment as EnrollmentType}
                sx={listItemSx}
              />
            )
        )}
        {limitRecentlyCompletedClasses &&
          enrollmentsForTranscript.length > limitRecentlyCompletedClasses && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center"
              }}
            >
              <Button
                variant={"outlined"}
                onClick={() => navigate(Routes.transcriptsPath())}
              >
                {t("View All Past Classes")}
              </Button>
            </Box>
          )}
      </Box>
    </>
  );
};
